<template>
  <div class="card">
    <!-- Toolbar Top Options -->
    <div class="toolbar">
      <div class="left-buttons">
        <router-link to="/nasabah/add">
          <button class="btn success" title="Tambah Data">➕ Tambah</button>
        </router-link>
        <button class="btn primary" @click="onDownload" title="Download Excel">⬇ Export</button>
      </div>

      <div class="filter-group">
        <!-- Filter tanggal pengajuan -->
        <div class="date-filter">
          <flat-pickr
            v-model="dateRangePengajuan"
            :config="dateConfig"
            placeholder="📅 Tgl Pengajuan"
            class="date-input"
          />
          <span
            v-if="hasDateRangePengajuan"
            class="clear-btn"
            @click="clearDate('pengajuan')"
            title="Hapus Filter"
          >✖</span>
        </div>

        <!-- Filter tanggal pencairan + tombol Belum Pencairan -->
        <div v-if="is_kode_bank == 2" class="pencairan-section">
          <div class="date-filter" v-if="!showBelumPencairan">
            <flat-pickr
              v-model="dateRangePencairan"
              :config="dateConfig"
              placeholder="📅 Tgl Pencairan"
              class="date-input"
            />
            <span
              v-if="hasDateRangePencairan"
              class="clear-btn"
              @click="clearDate('pencairan')"
              title="Hapus Filter"
            >✖</span>
          </div>

          <button
            class="btn warning"
            @click="onBelumPencairan()"
            :title="showBelumPencairan ? 'Tampilkan kembali filter tanggal pencairan' : 'Cari data yang belum pencairan'"
            :class="{ active: showBelumPencairan }"
          >
            ⏳ {{ showBelumPencairan ? 'Semua Status' : 'Belum Cair' }}
          </button>
        </div>

        <!-- Search Box -->
        <div class="search-box">
          <input type="text" v-model="search" placeholder="🔍 Cari nasabah..." />
        </div>
      </div>
    </div>

    <!-- Loading Indicator -->
    <div v-if="loading" class="loading-box">
      <div class="spinner"></div>
      <span>Memuat data...</span>
    </div>

    <!-- Table Data -->
    <div class="table-responsive" v-else>
      <table>
        <thead>
          <tr>
            <th style="width: 40px; text-align: center;">#</th>
            <th>NOPEN</th>
            <th>Nama Nasabah</th>
            <th>Tgl Pengajuan</th>
            <th>Tgl Pencairan</th>
            <th>Jenis Pengajuan</th>
            <th>Jenis Produk</th>
            <th>Kerja Nasabah</th>
            <th style="text-align: center; width: 110px;">Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in paginatedData" :key="item.id">
            <td style="text-align: center; font-weight: 600;">{{ (currentPage - 1) * perPage + index + 1 }}</td>
            <td><strong>{{ item.nopen }}</strong></td>
            <td>{{ item.nama_nasabah }}</td>
            <td>{{ item.tgl_pengajuan }}</td>
            <td>
              <span :class="['status-badge', item.tgl_pencairan && item.tgl_pencairan !== '0000-00-00' ? 'cair' : 'belum-cair']">
                {{ item.tgl_pencairan && item.tgl_pencairan !== '0000-00-00' ? item.tgl_pencairan : 'Belum Cair' }}
              </span>
            </td>
            <td>{{ item.pengajuan?.jenis_pengajuan || '-' }}</td>
            <td>{{ item.produk?.nama_produk || '-' }}</td>
            <td>{{ item.kerja_nasabah }}</td>
            <td style="text-align: center;">
              <div class="button-group">
                <button class="btn info" @click="onDetail(item.id)" title="Detail">🔍</button>
                <button class="btn warning" @click="onEdit(item.id)" title="Edit">📝</button>
                <button class="btn danger" @click="onDelete(item.id)" title="Hapus">❌</button>
              </div>
            </td>
          </tr>
          <tr v-if="filteredItems.length === 0">
            <td colspan="9" class="empty-state">
              Data nasabah tidak ditemukan
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="pagination-container" v-if="totalPages > 1">
      <div class="pagination-info">
        Menampilkan {{ (currentPage - 1) * perPage + 1 }} - {{ Math.min(currentPage * perPage, filteredItems.length) }} dari {{ filteredItems.length }} data
      </div>

      <div class="pagination">
        <button :disabled="currentPage === 1" @click="currentPage--" class="page-nav">⬅ Prev</button>

        <button
          v-if="pageRange[0] > 1"
          :class="['page-btn', { active: 1 === currentPage }]"
          @click="goToPage(1)"
        >
          1
        </button>
        <span v-if="pageRange[0] > 2" class="dots">...</span>

        <button
          v-for="page in pageRange"
          :key="page"
          :class="['page-btn', { active: page === currentPage }]"
          @click="goToPage(page)"
        >
          {{ page }}
        </button>

        <span v-if="pageRange[pageRange.length - 1] < totalPages - 1" class="dots">...</span>
        <button
          v-if="pageRange[pageRange.length - 1] < totalPages"
          :class="['page-btn', { active: totalPages === currentPage }]"
          @click="goToPage(totalPages)"
        >
          {{ totalPages }}
        </button>

        <button :disabled="currentPage === totalPages" @click="currentPage++" class="page-nav">
          Next ➡
        </button>
      </div>

      <div class="jump-to">
        <span>Lompat:</span>
        <input
          type="number"
          v-model.number="jumpPage"
          @keyup.enter="goToPage(jumpPage)"
          min="1"
          :max="totalPages"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import XLSX from "xlsx-js-style";
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
  components: { FlatPickr },
  data() {
    return {
      search: "",
      currentPage: 1,
      perPage: 10,
      items: [],
      loading: false,
      dateRangePengajuan: [],
      dateRangePencairan: [],
      dateConfig: {
        mode: "range",
        dateFormat: "Y-m-d",
      },
      jumpPage: null,
      is_kode_bank: JSON.parse(localStorage.getItem("user") || "{}").kode_bank,
      showBelumPencairan: false,
    };
  },
  computed: {
    hasDateRangePengajuan() {
      return Array.isArray(this.dateRangePengajuan)
        ? this.dateRangePengajuan.length > 0
        : !!this.dateRangePengajuan;
    },
    hasDateRangePencairan() {
      return Array.isArray(this.dateRangePencairan)
        ? this.dateRangePencairan.length > 0
        : !!this.dateRangePencairan;
    },
    filteredItems() {
      let data = this.items;

      if (this.search) {
        const keyword = this.search.toLowerCase();
        data = data.filter((u) =>
          Object.values(u).some(
            (val) =>
              typeof val === "string" && val.toLowerCase().includes(keyword)
          )
        );
      }

      if (this.hasDateRangePengajuan) {
        const { start, end } = this.extractDateRange(this.dateRangePengajuan);
        if (start && end) {
          data = data.filter((item) => {
            const tgl = this.formatToYMD(item.tgl_pengajuan);
            return tgl >= start && tgl <= end;
          });
        }
      }

      if (this.hasDateRangePencairan) {
        const { start, end } = this.extractDateRange(this.dateRangePencairan);
        if (start && end) {
          data = data.filter((item) => {
            const tgl = this.formatToYMD(item.tgl_pencairan);
            return tgl && tgl >= start && tgl <= end;
          });
        }
      }

      if (this.showBelumPencairan) {
        data = data.filter(
          (item) =>
            item.tgl_pencairan === "0000-00-00" ||
            !item.tgl_pencairan ||
            item.tgl_pencairan === "-"
        );
      }

      return data;
    },
    totalPages() {
      return Math.ceil(this.filteredItems.length / this.perPage) || 1;
    },
    pageRange() {
      const delta = 2;
      const range = [];
      const start = Math.max(2, this.currentPage - delta);
      const end = Math.min(this.totalPages - 1, this.currentPage + delta);
      for (let i = start; i <= end; i++) range.push(i);
      return range;
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredItems.slice(start, start + this.perPage);
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    extractDateRange(dateRange) {
      let start = "", end = "";
      if (Array.isArray(dateRange)) {
        start = this.formatToYMD(dateRange[0]);
        end = this.formatToYMD(dateRange[1] || dateRange[0]);
      } else if (typeof dateRange === "string") {
        const parts = dateRange.split(" to ");
        start = this.formatToYMD(parts[0]);
        end = this.formatToYMD(parts[1] || parts[0]);
      }
      return { start, end };
    },
    formatToYMD(val) {
      if (!val) return "";
      if (val instanceof Date && !isNaN(val)) return val.toISOString().slice(0, 10);
      if (typeof val === "string") {
        const parsed = new Date(val);
        if (!isNaN(parsed)) return parsed.toISOString().slice(0, 10);
      }
      return "";
    },
    clearDate(type) {
      if (type === "pengajuan") this.dateRangePengajuan = [];
      if (type === "pencairan") this.dateRangePencairan = [];
    },
    getData() {
      this.loading = true;
      fetch(`${this.$apiURL}/v1/data_nasabah`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
        body: JSON.stringify({
          kode_bank: JSON.parse(localStorage.getItem("user") || "{}").bank.id,
        }),
      })
      .then((res) => res.json())
      .then((result) => {
        this.loading = false;
        this.items = Array.isArray(result) ? result : [];
      })
      .catch((err) => {
        this.loading = false;
        console.error("Fetch error:", err);
      });
    },
    onBelumPencairan() {
      this.showBelumPencairan = !this.showBelumPencairan;
      this.currentPage = 1;

      if (this.showBelumPencairan) {
        this.dateRangePencairan = [];
      }
    },
    onEdit(id) {
      this.$router.push({ name: "nasabah_edit", state: { id } });
    },
    onDetail(id) {
      this.$router.push({ name: "nasabah_detail", state: { id_transaksi: id } });
    },
    onDelete(id) {
      Swal.fire({
        title: "Yakin ingin hapus data ?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#0b1953",
        confirmButtonText: "Ya, Hapus",
        cancelButtonText: "Batal",
      }).then((result) => {
        if (result.isConfirmed) {
          fetch(`${this.$apiURL}/v1/delete_nasabah`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
            body: JSON.stringify({ id }),
          })
            .then((r) => r.json())
            .then((res) => {
              if (res.success) this.getData();
            });
        }
      });
    },
    onDownload() {
      const exportData = this.filteredItems.map((item) => ({
        NOPEN: item.nopen,
        "Nama Nasabah": item.nama_nasabah,
        "Tanggal Pengajuan": item.tgl_pengajuan,
        "Tanggal Pencairan": item.tgl_pencairan,
        // Gunakan optional chaining (?.) untuk mencegah error null
        "Jenis Pengajuan": item.pengajuan?.jenis_pengajuan || "-",
        "Jenis Produk": item.produk?.nama_produk || item.produk?.jenis_produk || "-",
        "Kerja Nasabah": item.kerja_nasabah,
        Plafond: item.plafond,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const headers = Object.keys(exportData[0] || {});
      headers.forEach((_, i) => {
        const cell = XLSX.utils.encode_cell({ r: 0, c: i });
        if (ws[cell])
          ws[cell].s = {
            font: { bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "0B1953" } },
            alignment: { horizontal: "center", vertical: "center" },
          };
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "DataNasabah");
      XLSX.writeFile(wb, "data_nasabah.xlsx");
    },
    goToPage(page) {
      if (!page || page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    }
  },
};
</script>

<style scoped>
/* Toolbar Layout */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.left-buttons {
  display: flex;
  gap: 8px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.pencairan-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-box input {
  margin-bottom: 0;
  width: 200px;
}

/* Date Picker Style */
.date-filter {
  position: relative;
}

.date-input {
  margin-bottom: 0;
  padding-right: 28px;
  width: 170px;
  font-size: 0.85rem;
}

.clear-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #94a3b8;
  font-size: 12px;
}

.clear-btn:hover {
  color: #ef4444;
}

/* Status Badge */
.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-badge.cair {
  background-color: #d1fae5;
  color: #065f46;
}

.status-badge.belum-cair {
  background-color: #fef3c7;
  color: #92400e;
}

.btn.warning.active {
  background-color: #f47b20;
  color: #fff;
}

/* Loading Box */
.loading-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #0b1953;
  gap: 10px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: #f47b20;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 24px !important;
  color: #64748b;
  font-weight: 600;
}

/* Pagination Style */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #edf2f7;
  flex-wrap: wrap;
  gap: 12px;
}

.pagination-info {
  font-size: 0.85rem;
  color: #64748b;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 4px;
}

.dots {
  color: #94a3b8;
  padding: 0 4px;
}

.jump-to {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #64748b;
}

.jump-to input {
  width: 50px;
  margin-bottom: 0;
  padding: 4px 6px;
  text-align: center;
}
</style>